import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

type LogLevel = 'debug' | 'log' | 'warn' | 'error' | 'none';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly levelOrder: Record<LogLevel, number> = {
    debug: 10,
    log: 20,
    warn: 30,
    error: 40,
    none: 50
  };

  private readonly sensitiveKeys = new Set([
    'token',
    'accessToken',
    'refreshToken',
    'id_token',
    'authorization',
    'password',
    'secret',
    'apiKey',
    'api_key',
    'signature',
    'session',
    'sessionId',
    'csrf',
    'csrfToken',
    'email',
    'wallet',
    'walletAddress',
    'authToken'
  ].map((key) => key.toLowerCase()));

  // Logging helps debugging, but it is not a security boundary.
  // Real security still depends on backend validation, authorization,
  // and secure handling of credentials/tokens outside browser storage.
  log(message: string, ...meta: unknown[]): void {
    this.write('log', message, meta);
  }

  warn(message: string, ...meta: unknown[]): void {
    this.write('warn', message, meta);
  }

  error(message: string, ...meta: unknown[]): void {
    this.write('error', message, meta);
  }

  debug(message: string, ...meta: unknown[]): void {
    this.write('debug', message, meta);
  }

  private write(level: Exclude<LogLevel, 'none'>, message: string, meta: unknown[]): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const sanitizedMeta = meta.map((entry) => this.sanitize(entry));
    const logger = this.getConsoleMethod(level);
    logger(message, ...sanitizedMeta);
  }

  private shouldLog(level: Exclude<LogLevel, 'none'>): boolean {
    const loggingEnabled = (environment as Record<string, unknown>)['ENABLE_LOGGING'] !== false;
    const configuredLevel = ((environment as Record<string, unknown>)['LOG_LEVEL'] as LogLevel | undefined) ?? 'debug';

    if (environment.production || !loggingEnabled) {
      return false;
    }

    return this.levelOrder[level] >= (this.levelOrder[configuredLevel] ?? this.levelOrder.debug);
  }

  private getConsoleMethod(level: Exclude<LogLevel, 'none'>): (...args: unknown[]) => void {
    switch (level) {
      case 'debug':
        return console.debug.bind(console);
      case 'warn':
        return console.warn.bind(console);
      case 'error':
        return console.error.bind(console);
      default:
        return console.log.bind(console);
    }
  }

  private sanitize(value: unknown, depth: number = 0): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message
      };
    }

    if (typeof value === 'string') {
      return this.sanitizeString(value);
    }

    if (typeof value !== 'object') {
      return value;
    }

    if (depth > 4) {
      return '[Truncated]';
    }

    if (Array.isArray(value)) {
      return value.map((entry) => this.sanitize(entry, depth + 1));
    }

    const source = value as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};

    Object.keys(source).forEach((key) => {
      const raw = source[key];
      const normalizedKey = key.toLowerCase();

      if (this.sensitiveKeys.has(normalizedKey) || this.matchesSensitiveKey(normalizedKey)) {
        sanitized[key] = '[REDACTED]';
        return;
      }

      sanitized[key] = this.sanitize(raw, depth + 1);
    });

    return sanitized;
  }

  private matchesSensitiveKey(key: string): boolean {
    return ['token', 'password', 'secret', 'signature', 'auth', 'session', 'email', 'wallet'].some((fragment) => key.includes(fragment));
  }

  private sanitizeString(value: string): string {
    if (/^Bearer\s+/i.test(value)) {
      return 'Bearer [REDACTED]';
    }

    if (/^eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+$/.test(value)) {
      return '[REDACTED_TOKEN]';
    }

    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      const [localPart, domain] = value.split('@');
      return `${localPart.slice(0, 2)}***@${domain}`;
    }

    if (/^0x[a-fA-F0-9]{40,}$/.test(value)) {
      return `${value.slice(0, 6)}...${value.slice(-4)}`;
    }

    return value;
  }
}
